import React, { useState, useRef, useEffect, Suspense } from 'react';
import { ChevronRight, Wifi, BatteryFull, Lock } from 'lucide-react';
import { gizmoRuntime } from '@gizmo/runtime';

const CLOWNFISH_WALLPAPER_URL = "https://content.gizmo.party/0542a34f-0ceb-4d3b-80b6-1b385aacf47d";

const tweaks = gizmoRuntime.tweaks({
  unlockHapticStyle: {
    type: 'slider',
    value: 2, // Default to 'medium'
    min: 0,
    max: 4,
    step: 1,
    name: 'Unlock Haptic Style',
    index: 0,
  },
  resetHapticStyle: {
    type: 'slider',
    value: 0, // Default to 'light'
    min: 0,
    max: 4,
    step: 1,
    name: 'Reset Haptic Style',
    index: 1,
  },
  unlockThreshold: {
    type: 'slider',
    value: 0.95, // Default unlock threshold
    min: 0.5,
    max: 1.0,
    step: 0.01,
    name: 'Unlock Threshold',
    index: 2,
  },
});

// Helper to format time
const formatTime = (date: Date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const minutesStr = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minutesStr}`;
};

// Helper to format date
const formatDate = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'long', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

const StatusBar = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);

  return (
    <div className="absolute top-0 left-0 right-0 h-10 px-4 flex items-center justify-between text-white bg-black/30 backdrop-blur-sm z-10">
      <div className="flex items-center space-x-1">
        <span className="text-sm font-semibold">.ılı Cingular</span>
      </div>
      <div className="flex items-center space-x-2">
        <Lock size={14} />
        <span className="text-sm font-semibold">{formatTime(currentTime)}</span>
        <Wifi size={16} />
        <span className="text-sm font-semibold">83%</span> {/* Updated battery percentage */}
        <BatteryFull size={20} className="text-green-400" />
      </div>
    </div>
  );
};

const ClockDisplay = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timerId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);

  return (
    <div className="text-center text-white select-none">
      <h1 className="text-7xl font-thin tracking-tight">6:08</h1> {/* Hardcoded time for authenticity */}
      <p className="text-xl font-light">Monday, May 12</p> {/* Hardcoded date for authenticity */}
    </div>
  );
};

const ImageBackground = ({ src, children }: { src: string, children: React.ReactNode }) => {
  return (
    <div 
      className="w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${src})` }}
    >
      {children}
    </div>
  );
};

export default function Component() {
  const [sliderPosition, setSliderPosition] = useState(0);
  const [unlocked, setUnlocked] = useState(false);
  const [showShimmer, setShowShimmer] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const unlockHapticStyle = tweaks.unlockHapticStyle.useState();
  const resetHapticStyle = tweaks.resetHapticStyle.useState();
  const unlockThreshold = tweaks.unlockThreshold.useState();

  const hapticStyles = ['light', 'medium', 'heavy', 'soft', 'rigid'];

  const handleInteractionStart = (clientX: number) => {
    if (unlocked) return;
    const startX = clientX - sliderPosition;

    const handleInteractionMove = (currentClientX: number) => {
      let newPosition = currentClientX - startX;
      
      if (containerRef.current && sliderRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const sliderWidth = sliderRef.current.offsetWidth;
        // Adjusted maxPosition to account for padding inside the slider track
        const maxPosition = containerWidth - sliderWidth - 8; // 4px padding on each side of track

        if (newPosition < 0) newPosition = 0;
        if (newPosition > maxPosition) newPosition = maxPosition;
        
        setSliderPosition(newPosition);

        if (newPosition >= maxPosition * unlockThreshold) {
          setUnlocked(true);
          setShowShimmer(false);
          window.gizmoRuntime.performHaptic({style: hapticStyles[unlockHapticStyle] as any});
          setTimeout(() => setSliderPosition(maxPosition), 50);
        }
      }
    };

    const handleInteractionEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);

      if (!unlocked) {
        setSliderPosition(0);
        window.gizmoRuntime.performHaptic({style: hapticStyles[resetHapticStyle] as any});
      }
    };

    const handleMouseMove = (e: MouseEvent) => handleInteractionMove(e.clientX);
    const handleMouseUp = () => handleInteractionEnd();
    const handleTouchMove = (e: TouchEvent) => handleInteractionMove(e.touches[0].clientX);
    const handleTouchEnd = () => handleInteractionEnd();

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };
  
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault(); // Prevent page scroll
    handleInteractionStart(e.touches[0].clientX);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    handleInteractionStart(e.clientX);
  };


  useEffect(() => {
    let shimmerInterval: NodeJS.Timeout;
    if (showShimmer && !unlocked) {
      // The shimmer animation is CSS-driven, this interval is just for potential JS-driven effects if needed
    }
    return () => clearInterval(shimmerInterval);
  }, [showShimmer, unlocked]);

  if (unlocked) {
    return (
      <div className="flex items-center justify-center h-screen w-full bg-black">
        <img 
          src="https://content.gizmo.party/9d6029bb-99ed-4377-ab31-ef98043fd024" 
          alt="Unlocked content" 
          className="object-cover h-full w-full"
          crossOrigin="anonymous"
        />
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="h-screen w-full flex items-center justify-center bg-slate-800"><p className="text-white">Loading Wallpaper...</p></div>}>
      <div className="relative flex flex-col items-center justify-between h-screen overflow-hidden">
        <ImageBackground src={CLOWNFISH_WALLPAPER_URL}>
          <StatusBar />
          <div className="flex-grow flex flex-col items-center justify-center pt-10"> {/* pt-10 to push clock down from status bar */}
            <ClockDisplay />
          </div>
          <div className="w-full flex justify-center absolute bottom-12 left-0 right-0 px-4"> {/* Position at the bottom with horizontal padding */}
            <div 
              ref={containerRef}
              className="relative w-full max-w-[300px] h-14 rounded-full p-1 bg-black/40 backdrop-blur-sm shadow-xl" // Rounded full corners
            >
              <div
                ref={sliderRef}
                className={`absolute top-1 left-1 w-16 h-12 rounded-full flex items-center justify-center // Rounded full corners
                            bg-white/60 cursor-grab active:cursor-grabbing
                            shadow-lg transition-all duration-100 ease-out`}
                style={{ transform: `translateX(${sliderPosition}px)` }}
                onTouchStart={handleTouchStart}
                onMouseDown={handleMouseDown}
                role="slider"
                aria-valuemin={0}
                aria-valuemax={100} 
                aria-valuenow={(sliderPosition / ((containerRef.current?.offsetWidth || 300) - (sliderRef.current?.offsetWidth || 64) - 8) * 100)} // Adjusted max width
                aria-label="Slide to unlock"
              >
                <ChevronRight size={32} className="text-slate-800" />
              </div>
              {showShimmer && (
                <div 
                  className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full pointer-events-none" // Rounded full corners
                  aria-hidden="true"
                >
                  <span 
                    className="text-lg font-medium text-white/90 shimmer-text-ios"
                    style={{
                      opacity: Math.max(0, 1 - (sliderPosition / 60)) 
                    }}
                  >
                    slide to unlock
                  </span>
                </div>
              )}
            </div>
          </div>
        </ImageBackground>
        <style jsx global>{`
          .shimmer-text-ios {
            background: linear-gradient(90deg, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.2) 80%);
            background-size: 250% 100%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: shimmer-ios 3s infinite linear;
            padding-left: 40px; /* To make space for the arrow */
          }
          @keyframes shimmer-ios {
            0% { background-position: 150% 0; }
            100% { background-position: -150% 0; }
          }
          /* Ensure full screen experience */
          html, body, #__next {
            height: 100%;
            margin: 0;
            padding: 0;
            overflow: hidden; /* Prevent scrollbars on the body */
          }
        `}</style>
      </div>
    </Suspense>
  );
}



